import { openEvolutionModal, responseData } from 'Pages/AnalyzersV2/@signals'
import { demoPopoverOpen } from './signals'

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findByTooltip(text) {
	const els = document.querySelectorAll('[aria-label], [title]')
	for (const el of els) {
		const label = (el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase()
		if (label.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function hasRealData() {
	return Object.keys(responseData.value || {}).length > 0
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	// 0 — Intro
	{
		type: 'intro',
		icon: 'globe',
		title: ES ? 'Bienvenido al Analizador de Sitio Directo' : 'Welcome to the Direct Site Analyzer',
		description: ES
			? 'El Analizador de Sitio Directo compara los precios de tu web directa frente a los precios de OTAs y brokers. Identifica dónde tu canal directo es más o menos competitivo.'
			: 'The Direct Site Analyzer compares your direct website prices against OTA and broker prices. Identify where your direct channel is more or less competitive.',
	},
	// 1 — Report Selector
	{
		type: 'field',
		skipIf: () => !document.querySelector('input[placeholder="Please, select a report"]'),
		findTarget: () => {
			const input = document.querySelector('input[placeholder="Please, select a report"]')
			return input?.closest('.MuiAutocomplete-root') || input?.closest('.MuiFormControl-root')
		},
		icon: 'file-lines',
		title: ES ? 'Selector de Informe' : 'Report Selector',
		description: ES
			? 'Selecciona una ejecución de informe de este desplegable para cargar los datos del análisis. Los informes de Sitio Directo comparan los precios de tu propia web frente a distribuidores externos.'
			: 'Select a report execution from this dropdown to load the analysis data. Direct Site reports compare your own website prices against third-party distributors.',
	},
	// 2 — Price Table
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Analyzer: Price Table')
			return el?.closest('.MuiCard-root') || el?.closest('.MuiGrid-item')
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de Precios' : 'Price Table',
		description: ES
			? 'La tabla comparativa principal. Cada fila es un sitio web/proveedor mostrando precios del sitio directo frente a OTAs. Las celdas con código de color destacan las opciones más baratas (verde) y más caras (rojo).'
			: 'The main comparison table. Each row is a website/provider showing direct site vs OTA prices. Color-coded cells highlight the cheapest (green) and most expensive (red) options.',
		example: ES ? 'Verde = Más barato | Rojo = Más caro' : 'Green = Cheapest | Red = Most Expensive',
	},
	// 3 — Toggle Details
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Hide Details')
			return el?.parentElement
		},
		icon: 'toggle-on',
		title: ES ? 'Mostrar/Ocultar Detalles' : 'Toggle Details',
		description: ES
			? 'Muestra u oculta detalles adicionales en la tabla como modelo de coche, categoría, código ACRISS, transmisión y política de combustible para cada entrada de precio.'
			: 'Show or hide extra details in the table like car model, category, ACRISS code, transmission, and fuel policy for each price entry.',
	},
	// 4 — Features Selector
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Features')
			return el?.closest('.MuiFormControl-root') || el?.parentElement
		},
		icon: 'filter',
		title: ES ? 'Selector de Características' : 'Features Selector',
		description: ES
			? 'Elige qué columnas mostrar en la tabla — Proveedor, Modelo de Coche, Categoría, ACRISS, Precio, Transmisión, Política de Combustible, Pago y más.'
			: 'Choose which columns to display in the table — Provider, Car Model, Category, ACRISS, Price, Transmission, Fuel Policy, Payment, and more.',
		example: ES ? 'Selecciona: Proveedor, Categoría, Precio, ACRISS' : 'Select: Provider, Category, Price, ACRISS',
	},
	// 5 — Min / Max View
	{
		type: 'field',
		findTarget: () => {
			const els = document.querySelectorAll('.MuiToggleButtonGroup-root')
			for (const el of els) {
				if (el.textContent.toLowerCase().includes('min')) return el
			}
			return null
		},
		icon: 'arrow-down-arrow-up',
		title: ES ? 'Vista Mín / Máx' : 'Min / Max View',
		description: ES
			? 'Alterna entre la vista de precio mínimo (opción más barata por proveedor) y precio máximo (la más cara). Compara tu rango de precios directos frente a los competidores.'
			: 'Switch between the minimum price (cheapest option per provider) and maximum price (most expensive) view. Compare your direct pricing range against competitors.',
	},
	// 6 — Price Cell
	// action: opens the popover (demo signal or real click), waitFor: popover in DOM
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-price-cell"]') ||
			(() => {
				const rows = document.querySelectorAll('table tbody tr')
				for (const row of rows) {
					const cells = row.querySelectorAll('td')
					if (cells.length > 1) return cells[1]
				}
				return null
			})()
		),
		action: () => {
			if (!hasRealData()) {
				// Demo case: open demo popover via signal
				demoPopoverOpen.value = true
			} else {
				// Real data case: click the inner Box of the first price cell (the one with onClick)
				const rows = document.querySelectorAll('table tbody tr')
				for (const row of rows) {
					const cells = row.querySelectorAll('td')
					if (cells.length > 1) {
						const clickTarget = cells[1].querySelector('.MuiBox-root') || cells[1]
						clickTarget?.click()
						break
					}
				}
			}
		},
		waitFor: () =>
			!!document.querySelector('[data-tour-id="tour-demo-popover"]') ||
			!!document.querySelector('.MuiPopover-paper'),
		icon: 'hand-pointer',
		title: ES ? 'Celda de Precio' : 'Price Cell',
		description: ES
			? 'Haz clic en cualquier celda de precio para abrir un panel con el detalle de esa oferta: nombre del vehículo, características y accesos directos a herramientas de análisis.'
			: 'Click on any price cell to open a detail panel for that offer: vehicle name, features, and shortcuts to analysis tools.',
	},
	// 7 — Popover overview
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-demo-popover"]') ||
			document.querySelector('.MuiPopover-paper')
		),
		icon: 'car',
		title: ES ? 'Detalle del Vehículo' : 'Vehicle Details',
		description: ES
			? 'El panel muestra el nombre del vehículo y sus características: categoría, política de combustible, tipo de pago y fecha de recogida. Datos exactos capturados por el rastreador en esa ejecución.'
			: 'The panel shows the vehicle name and its features: category, fuel policy, payment type, and pickup date. Exact data captured by the scraper during that execution.',
	},
	// 8 — Evolution button (green)
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-popover-evolution"]'),
		icon: 'chart-line',
		title: ES ? 'Botón Evolución' : 'Evolution Button',
		description: ES
			? 'Abre el gráfico de evolución histórica de precios para esa celda concreta. Muestra cómo ha variado el precio de ese vehículo en ese proveedor a lo largo del tiempo.'
			: 'Opens the historical price evolution chart for that specific cell. Shows how the price for that vehicle at that provider has changed over time.',
	},
	// 9 — Real-time Search button (orange)
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-popover-realtime"]'),
		icon: 'magnifying-glass',
		title: ES ? 'Botón Búsqueda en Tiempo Real' : 'Real-Time Search Button',
		description: ES
			? 'Abre la búsqueda en Real-Time con todos los filtros de esta celda precargados. Solo disponible si el rastreador encontró datos de búsqueda correspondientes.'
			: 'Opens the Real-Time search with all filters for this cell pre-loaded. Only available if the scraper found corresponding search data.',
	},
	// 10 — Screenshot button (blue)
	// action: close popover (demo signal + real popover close button) when moving to step 11
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-popover-screenshot"]'),
		action: () => {
			demoPopoverOpen.value = false
			document.querySelector('[data-tour-id="tour-popover-close"]')?.click()
		},
		icon: 'image',
		title: ES ? 'Botón Captura de Pantalla' : 'Screenshot Button',
		description: ES
			? 'Muestra la captura de pantalla tomada por el rastreador en el momento de la extracción. Solo disponible si el rastreador guardó una imagen para esta oferta.'
			: 'Shows the screenshot captured by the scraper at the time of extraction. Only available if the scraper saved an image for this offer.',
	},
	// 11 — Column Header
	// action: open evolution modal (real click or demoMode), waitFor: fullscreen dialog
	// reverseAction: re-open demo popover when going back from here to step 10
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-column-header"]') ||
			(() => {
				const headers = document.querySelectorAll('table thead th')
				return headers.length > 1 ? headers[1] : null
			})()
		),
		action: () => {
			if (!hasRealData()) {
				openEvolutionModal.value = { demoMode: true }
			} else {
				// Click the first data column header (AnalyzerHeaderTable renders a Box with onClick)
				const headers = document.querySelectorAll('table thead th')
				if (headers.length > 1) {
					const clickable = headers[1].querySelector('.MuiBox-root') || headers[1]
					clickable?.click()
				}
			}
		},
		waitFor: () => !!document.querySelector('.MuiDialog-paperFullScreen'),
		reverseAction: () => {
			// Re-open popover when going back to step 10
			if (!hasRealData()) {
				demoPopoverOpen.value = true
			} else {
				// For real data: re-click the price cell to re-open the real popover
				const rows = document.querySelectorAll('table tbody tr')
				for (const row of rows) {
					const cells = row.querySelectorAll('td')
					if (cells.length > 1) {
						const clickTarget = cells[1].querySelector('.MuiBox-root') || cells[1]
						clickTarget?.click()
						break
					}
				}
			}
		},
		icon: 'table-columns',
		title: ES ? 'Cabecera de Columna' : 'Column Header',
		description: ES
			? 'Haz clic en el encabezado de una columna para abrir el gráfico de evolución comparando tu sitio directo frente a los distribuidores en ese tipo de precio. Ideal para detectar si tu canal directo está por encima o por debajo del mercado en un segmento concreto.'
			: 'Click on any column header to open the evolution chart comparing your direct site against distributors for that price type. Ideal for spotting whether your direct channel is above or below market in a specific segment.',
	},
	// 12 — Evolution Chart overview (modal OPEN)
	// action: close modal when moving to step 13
	// cleanup: safety net if tour is closed while at this step
	{
		type: 'field',
		findTarget: () => document.querySelector('.MuiDialog-paperFullScreen'),
		action: () => {
			openEvolutionModal.value = {}
		},
		cleanup: () => {
			openEvolutionModal.value = {}
		},
		icon: 'chart-line',
		title: ES ? 'Gráfico de Evolución' : 'Evolution Chart',
		description: ES
			? 'Vista completa de la evolución de precios para todos los distribuidores en el tipo de precio seleccionado. Muestra tendencias a lo largo del tiempo con líneas por proveedor, permitiendo identificar si tu canal directo está perdiendo o ganando competitividad.'
			: 'Full view of price evolution for all distributors at the selected price type. Shows trends over time with lines per provider, letting you identify whether your direct channel is losing or gaining competitiveness.',
	},
	// 13 — Price Chart
	{
		type: 'field',
		findTarget: () => {
			const els = document.querySelectorAll('.MuiToggleButtonGroup-root')
			for (const el of els) {
				if (el.textContent.toLowerCase().includes('price values')) return el
			}
			return null
		},
		icon: 'chart-bar',
		title: ES ? 'Gráfico de Precios' : 'Price Chart',
		description: ES
			? 'Comparación visual de los precios del sitio directo frente a OTAs/brokers. Alterna entre valores de precio absolutos y variación porcentual para identificar diferencias de precios.'
			: 'Visual comparison of direct site prices vs OTA/broker prices. Toggle between absolute price values and percentage variation to identify pricing gaps.',
		example: ES ? 'Valores de Precio = importes en EUR | Variación = % de cambio' : 'Price Values = EUR amounts | Variation = % change',
	},
	// 14 — Fullscreen Chart
	{
		type: 'field',
		findTarget: () => findByTooltip('Fullscreen'),
		icon: 'expand',
		title: ES ? 'Gráfico en Pantalla Completa' : 'Fullscreen Chart',
		description: ES
			? 'Amplía el gráfico a pantalla completa para una vista más clara. Especialmente útil al comparar muchos proveedores o puntos de precio.'
			: 'Expand the chart to full screen for a clearer view. Especially useful when comparing many providers or price points.',
	},
	// 15 — Categories Table (specific to Direct Sites)
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Categories')
			return el?.closest('.MuiCard-root') || el?.closest('.MuiGrid-item')
		},
		icon: 'layer-group',
		title: ES ? 'Tabla de Categorías' : 'Categories Table',
		description: ES
			? 'Desglose de precios por categoría de vehículo (Económico, Compacto, SUV, etc.). Compara cómo varían los precios de tu sitio directo en distintos segmentos de coches frente a la competencia.'
			: 'Breakdown of prices by vehicle category (Economy, Compact, SUV, etc.). Compare how your direct site pricing varies across different car segments vs competitors.',
	},
	// 16 — Outro
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Haz clic en cualquier celda de precio para ver su evolución histórica. Usa los botones de navegación de analizadores de arriba para cambiar entre los analizadores LOR, DOR, Brokers, Sitio Directo y Rating.'
			: 'Click on any price cell to see its historical evolution. Use the analyzer navigation buttons above to switch between LOR, DOR, Brokers, Direct Site, and Rating analyzers.',
	},
]
